import api from './api';

/**
 * Onboarding Service
 * Handles all onboarding-related API calls
 * 
 * @author Financial Insights Platform
 * @version 1.0
 */
export const onboardingService = {
  // ==================== PROGRESS TRACKING ====================

  /**
   * Get current user's onboarding progress
   * @returns {Promise<Object>} - Onboarding progress data
   * @example
   * {
   *   userId: "123e4567-e89b-12d3-a456-426614174000",
   *   currentStep: 3,
   *   totalSteps: 6,
   *   percentComplete: 50,
   *   onboardingCompleted: false,
   *   role: "INVESTOR",
   *   createdAt: "2024-01-15T10:30:00",
   *   updatedAt: "2024-01-15T11:45:00",
   *   message: "Onboarding progress retrieved successfully"
   * }
   */
  async getProgress() {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please login first.');
    }
    return api.get('/onboarding/data');
  },

  /**
   * Get specific user's onboarding progress
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Onboarding progress data
   */
  async getUserProgress(userId) {
    return api.get(`/onboarding/${userId}/data`);
  },

  /**
   * Update current onboarding step for current user
   * @param {Object} stepData - Step update data
   * @param {number} stepData.currentStep - Current step number (0-based)
   * @param {boolean} stepData.completed - Whether onboarding is completed
   * @param {string} [stepData.stepData] - Optional JSON data for the step
   * @returns {Promise<Object>} - Updated progress data
   * @example
   * await onboardingService.updateStep({
   *   currentStep: 3,
   *   completed: false,
   *   stepData: JSON.stringify({ occupation: "employed" })
   * });
   */
  async updateStep(stepData) {
    return api.put('/onboarding/step', stepData);
  },

  /**
   * Update onboarding step for specific user
   * @param {string} userId - User ID
   * @param {Object} stepData - Step update data
   * @returns {Promise<Object>} - Updated progress data
   */
  async updateUserStep(userId, stepData) {
    return api.put(`/onboarding/${userId}/step`, stepData);
  },

  // ==================== COMPLETE ONBOARDING ====================

  async markOnboardingCompleted(userId, completionData) {
     return api.put(`/onboarding/${userId}/step`, completionData);
  },
  /**
   * Complete onboarding process with all collected data
   * @param {Object} onboardingData - Complete onboarding data
   * @returns {Promise<Object>} - Completion response
   * @example
   * // Investor example
   * await onboardingService.completeOnboarding({
   *   role: "INVESTOR",
   *   fullName: "John Doe",
   *   email: "john@example.com",
   *   firstName: "John",
   *   lastName: "Doe",
   *   phoneNumber: "+1-555-1234",
   *   address: {
   *     addressLine1: "123 Main St",
   *     city: "New York",
   *     state: "NY",
   *     zipCode: "10001",
   *     country: "USA"
   *   },
   *   occupation: {
   *     occupationType: "employed",
   *     company: "Goldman Sachs",
   *     jobTitle: "Investment Manager",
   *     details: "Managing portfolios"
   *   },
   *   investmentGoals: ["Long-term growth", "Retirement planning"],
   *   riskTolerance: "Moderate",
   *   portfolioSize: "$100K - $500K",
   *   investmentExperience: "Intermediate",
   *   sectors: ["Technology", "Healthcare"],
   *   portfolioAccounts: [...]
   * });
   */
  async completeOnboarding(onboardingData) {
    return api.post('/onboarding/complete', onboardingData);
  },

  /**
   * Complete onboarding for specific user
   * @param {string} userId - User ID
   * @param {Object} onboardingData - Complete onboarding data
   * @returns {Promise<Object>} - Completion response
   */
  async completeUserOnboarding(userId, onboardingData) {
    return api.post(`/onboarding/${userId}/complete`, onboardingData);
  },

  // ==================== SAVED DATA RETRIEVAL ====================

  /**
   * Get saved onboarding data for current user
   * @returns {Promise<Object>} - Saved onboarding data
   */
  async getSavedData() {
    return api.get('/onboarding/data');
  },

  /**
   * Get saved onboarding data for specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Saved onboarding data
   */
  async getUserSavedData(userId) {
    return api.get(`/onboarding/${userId}/data`);
  },

  // ==================== PARTIAL DATA SAVE ====================

  /**
   * Save partial onboarding data (for resuming later)
   * @param {Object} partialData - Partial onboarding data to save
   * @returns {Promise<Object>} - Save response
   * @example
   * await onboardingService.savePartialData({
   *   firstName: "John",
   *   lastName: "Doe",
   *   phoneNumber: "+1-555-1234",
   *   occupation: {
   *     occupationType: "employed",
   *     company: "Goldman Sachs"
   *   }
   * });
   */
  async savePartialData(partialData) {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please login first.');
    }
    return api.patch('/onboarding/data', partialData);
  },

  /**
   * Save partial onboarding data for specific user
   * @param {string} userId - User ID
   * @param {Object} partialData - Partial onboarding data to save
   * @returns {Promise<Object>} - Save response
   */
  async saveUserPartialData(userId, partialData) {
    return api.post(`/onboarding/${userId}/save`, partialData);
  },

  // ==================== VALIDATION ====================

  /**
   * Validate onboarding data before submission
   * @param {Object} onboardingData - Data to validate
   * @returns {Promise<Object>} - Validation response
   */
  async validateData(onboardingData) {
    return api.post('/onboarding/validate', onboardingData);
  },

  // ==================== INVESTOR-SPECIFIC ====================

  /**
   * Submit investor-specific onboarding data
   * @param {Object} investorData - Investor onboarding data
   * @param {string} investorData.fullName - Full name
   * @param {string} investorData.email - Email address
   * @param {Array<string>} investorData.investmentGoals - Investment goals
   * @param {string} investorData.riskTolerance - Risk tolerance level
   * @param {string} investorData.portfolioSize - Portfolio size range
   * @param {string} investorData.investmentExperience - Experience level
   * @param {Array<string>} investorData.sectors - Sectors of interest
   * @param {Array<Object>} investorData.portfolioAccounts - Portfolio accounts with positions
   * @returns {Promise<Object>} - Completion response
   */
  async completeInvestorOnboarding(investorData) {
    return this.completeOnboarding({
      role: 'INVESTOR',
      ...investorData,
    });
  },

  // ==================== ANALYST-SPECIFIC ====================

  /**
   * Submit analyst-specific onboarding data
   * @param {Object} analystData - Analyst onboarding data
   * @param {string} analystData.fullName - Full name
   * @param {string} analystData.email - Email address
   * @param {Array<string>} analystData.analyticalExpertise - Areas of expertise
   * @param {string} analystData.yearsExperience - Years of experience
   * @param {Array<string>} analystData.focusSectors - Focus sectors
   * @param {Array<string>} analystData.certifications - Professional certifications
   * @param {string} analystData.researchApproach - Research approach description
   * @param {Array<Object>} analystData.feeStructureOptions - Fee structure options
   * @param {boolean} analystData.freeConsultation - Offers free consultation
   * @param {number} analystData.consultationDuration - Consultation duration in minutes
   * @param {boolean} analystData.acceptingNewClients - Accepting new clients
   * @returns {Promise<Object>} - Completion response
   */
  async completeAnalystOnboarding(analystData) {
    return this.completeOnboarding({
      role: 'ANALYST',
      ...analystData,
    });
  },

  // ==================== ADVISOR-SPECIFIC ====================

  /**
   * Submit advisor-specific onboarding data
   * @param {Object} advisorData - Advisor onboarding data
   * @param {string} advisorData.fullName - Full name
   * @param {string} advisorData.email - Email address
   * @param {Array<string>} advisorData.advisorCertifications - Professional certifications
   * @param {Array<string>} advisorData.specializations - Areas of specialization
   * @param {string} advisorData.yearsAdvising - Years of advising experience
   * @param {Array<string>} advisorData.clientTypes - Types of clients served
   * @param {string} advisorData.advisoryApproach - Advisory approach description
   * @param {Array<Object>} advisorData.feeStructureOptions - Fee structure options
   * @param {boolean} advisorData.freeConsultation - Offers free consultation
   * @param {number} advisorData.consultationDuration - Consultation duration in minutes
   * @param {boolean} advisorData.acceptingNewClients - Accepting new clients
   * @returns {Promise<Object>} - Completion response
   */
  async completeAdvisorOnboarding(advisorData) {
    return this.completeOnboarding({
      role: 'ADVISOR',
      ...advisorData,
    });
  },

  // ==================== HELPER METHODS ====================

  /**
   * Check if user has completed onboarding
   * @returns {Promise<boolean>} - True if onboarding is completed
   */
  async isOnboardingComplete() {
    try {
      const progress = await this.getProgress();
      return progress.onboardingCompleted === true;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false;
    }
  },

  /**
   * Get onboarding completion percentage
   * @returns {Promise<number>} - Completion percentage (0-100)
   */
  async getCompletionPercentage() {
    try {
      const progress = await this.getProgress();
      return progress.percentComplete || 0;
    } catch (error) {
      console.error('Error getting completion percentage:', error);
      return 0;
    }
  },

  /**
   * Reset onboarding progress (start over)
   * @returns {Promise<Object>} - Reset response
   */
  async resetOnboarding() {
    return this.updateStep({
      currentStep: 0,
      completed: false,
    });
  },

  /**
   * Skip to specific step
   * @param {number} stepNumber - Step number to skip to
   * @returns {Promise<Object>} - Updated progress
   */
  async skipToStep(stepNumber) {
    return this.updateStep({
      currentStep: stepNumber,
      completed: false,
    });
  },

  // ==================== DATA TRANSFORMATION ====================

  /**
   * Prepare onboarding data for API submission
   * Transforms frontend data structure to backend-compatible format
   * @param {Object} formData - Form data from onboarding flow
   * @returns {Object} - Transformed data ready for API
   */
  prepareDataForSubmission(formData) {
    // Transform role to uppercase if needed
    const role = formData.role ? formData.role.toUpperCase() : null;

    // Base data structure
    const preparedData = {
      role,
      fullName: formData.fullName,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber,
      linkedInUrl: formData.linkedInUrl,
    };

    // Add address if present (structured object)
    if (formData.address) {
      preparedData.address = {
        addressLine1: formData.address.addressLine1,
        addressLine2: formData.address.addressLine2,
        city: formData.address.city,
        state: formData.address.state,
        zipCode: formData.address.zipCode,
        country: formData.address.country,
      };
    } else if (formData.addressLine1) {
      // Fallback: construct address from flat fields
      preparedData.address = {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      };
    }

    // Add occupation if present (structured object)
    if (formData.occupation) {
      preparedData.occupation = {
        occupationType: formData.occupation.occupationType,
        company: formData.occupation.company,
        jobTitle: formData.occupation.jobTitle,
        details: formData.occupation.details,
      };
    } else if (formData.occupationType) {
      // Fallback: construct occupation from flat fields
      preparedData.occupation = {
        occupationType: formData.occupationType,
        company: formData.company,
        jobTitle: formData.jobTitle,
        details: formData.occupationDetails,
      };
    }

    // Add role-specific data
    if (role === 'INVESTOR') {
      Object.assign(preparedData, {
        investmentGoals: formData.investmentGoals,
        riskTolerance: formData.riskTolerance,
        portfolioSize: formData.portfolioSize,
        investmentExperience: formData.investmentExperience,
        sectors: formData.sectors,
        portfolioAccounts: formData.portfolioAccounts,
      });
    } else if (role === 'ANALYST') {
      Object.assign(preparedData, {
        analyticalExpertise: formData.analyticalExpertise,
        yearsExperience: formData.yearsExperience,
        focusSectors: formData.focusSectors,
        certifications: formData.certifications,
        researchApproach: formData.researchApproach,
        feeStructureOptions: formData.feeStructureOptions,
        freeConsultation: formData.freeConsultation,
        consultationDuration: formData.consultationDuration,
        acceptingNewClients: formData.acceptingNewClients,
      });
    } else if (role === 'ADVISOR') {
      Object.assign(preparedData, {
        advisorCertifications: formData.advisorCertifications,
        specializations: formData.specializations,
        yearsAdvising: formData.yearsAdvising,
        clientTypes: formData.clientTypes,
        advisoryApproach: formData.advisoryApproach,
        feeStructureOptions: formData.feeStructureOptions,
        freeConsultation: formData.freeConsultation,
        consultationDuration: formData.consultationDuration,
        acceptingNewClients: formData.acceptingNewClients,
      });
    }

    // Remove undefined/null values
    return Object.fromEntries(
      Object.entries(preparedData).filter(([_, v]) => v !== undefined && v !== null)
    );
  },

  /**
   * Parse API response data to frontend format
   * @param {Object} apiData - Data from API
   * @returns {Object} - Transformed data for frontend
   */
  parseApiResponse(apiData) {
    return {
      ...apiData,
      role: apiData.role ? apiData.role.toLowerCase() : null,
      
      // Parse address if it's a structured object
      address: apiData.address ? {
        addressLine1: apiData.address.addressLine1,
        addressLine2: apiData.address.addressLine2,
        city: apiData.address.city,
        state: apiData.address.state,
        zipCode: apiData.address.zipCode,
        country: apiData.address.country,
      } : undefined,
      
      // Parse occupation if it's a structured object
      occupation: apiData.occupation ? {
        occupationType: apiData.occupation.occupationType,
        company: apiData.occupation.company,
        jobTitle: apiData.occupation.jobTitle,
        details: apiData.occupation.details,
      } : undefined,
    };
  },

  // ==================== LOCAL STORAGE HELPERS ====================

  /**
   * Save onboarding progress to localStorage
   * @param {number} step - Current step
   * @param {Object} data - Current onboarding data
   */
  saveToLocalStorage(step, data) {
    try {
      localStorage.setItem('onboardingStep', step.toString());
      localStorage.setItem('onboardingData', JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  /**
   * Get onboarding progress from localStorage
   * @returns {Object|null} - Saved progress or null
   */
  getFromLocalStorage() {
    try {
      const step = localStorage.getItem('onboardingStep');
      const data = localStorage.getItem('onboardingData');
      
      if (step && data) {
        return {
          step: parseInt(step, 10),
          data: JSON.parse(data),
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },

  /**
   * Clear onboarding data from localStorage
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem('onboardingStep');
      localStorage.removeItem('onboardingData');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },

  /**
   * Mark onboarding as completed in localStorage
   */
  markCompletedInLocalStorage() {
    try {
      localStorage.setItem('onboardingCompleted', 'true');
      this.clearLocalStorage(); // Clear step data
    } catch (error) {
      console.error('Error marking completed in localStorage:', error);
    }
  },

  /**
   * Check if onboarding is completed in localStorage
   * @returns {boolean} - True if completed
   */
  isCompletedInLocalStorage() {
    try {
      return localStorage.getItem('onboardingCompleted') === 'true';
    } catch (error) {
      console.error('Error checking localStorage:', error);
      return false;
    }
  },
};

export default onboardingService;
